<template>
  <div class="sidebar-wrapper">
  <div ref="sidebarRef" class="floating-sidebar" :class="{ expanded: isExpanded }">
    <!-- Logo -->
    <div class="sb-logo">
      <svg width="31" height="32" viewBox="0 0 31 32" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g clip-path="url(#clip0_1_2775)">
          <path fill-rule="evenodd" clip-rule="evenodd" d="M15.5 0C6.93959 0 0 6.92466 0 15.4667V16.5333C0 25.0753 6.93959 32 15.5 32C24.0604 32 31 25.0753 31 16.5333V15.4667C31 6.92466 24.0604 0 15.5 0ZM17.8138 24.9838C21.5788 24.2433 23.8443 19.6605 22.874 14.7475C21.9036 9.83468 18.0649 6.45224 14.2999 7.19266C10.5349 7.9331 8.26938 12.5161 9.23972 17.4289C10.2101 22.3418 14.0488 25.7242 17.8138 24.9838Z" fill="currentColor"/>
        </g>
        <defs>
          <clipPath id="clip0_1_2775">
            <rect width="31" height="32" fill="white"/>
          </clipPath>
        </defs>
      </svg>
    </div>

    <!-- Main Navigation -->
    <nav class="sb-nav">
      <template v-for="item in computedNavItems" :key="item.name">
        <button
          class="sb-btn"
          :class="{ active: isActive(item.route) }"
          @click="navigateTo(item.route)"
          :title="item.label"
        >
          <span v-if="item.svg" class="sb-btn-icon" v-html="item.svg" />
          <span v-else class="sb-btn-icon">
            <component :is="item.icon" :size="24" weight="regular" />
          </span>
          <span class="sb-btn-label">{{ item.label }}</span>
        </button>
        <button
          v-for="child in (item.children || [])"
          :key="child.name"
          class="sb-btn sb-btn--sub"
          :class="{ active: isActive(child.route) }"
          @click="navigateTo(child.route)"
          :title="child.label"
        >
          <span class="sb-btn-icon sb-btn-icon--sub">
            <span class="sb-sub-dot" />
          </span>
          <span class="sb-btn-label">{{ child.label }}</span>
        </button>
      </template>
    </nav>

    <!-- Bottom Section -->
    <div class="sb-bottom">
      <button
        v-for="item in computedFooterItems"
        :key="item.name"
        class="sb-btn"
        :class="{ active: isActive(item.route) }"
        @click="navigateTo(item.route)"
        :title="item.label"
      >
        <span class="sb-btn-icon">
          <component :is="item.icon" :size="24" weight="regular" />
        </span>
        <span class="sb-btn-label">{{ item.label }}</span>
      </button>

      <!-- Toggle expand/collapse -->
      <button class="sb-btn sb-toggle" @click="toggleSidebar" title="Expand menu">
        <span class="sb-btn-icon">
          <PhCaretRight :size="24" weight="regular" :class="{ 'sb-toggle-rotated': isExpanded }" />
        </span>
        <span class="sb-btn-label">{{ t('nav.collapse') }}</span>
      </button>

      <div class="sb-divider" />

      <div class="sb-avatar" @click="navigateTo('/app/settings')" title="Settings">
        <img :src="userAvatar || profileAvatarSvg" alt="Profile" />
      </div>
    </div>
  </div>

  <!-- Mobile Bottom Navigation -->
  <nav class="mobile-nav" aria-label="Bottom navigation">
    <button
      v-for="item in mobileBottomItems"
      :key="item.activeKey"
      type="button"
      class="mobile-nav-item"
      :class="{ active: isMobileNavActive(item.activeKey) }"
      :aria-current="isMobileNavActive(item.activeKey) ? 'page' : undefined"
      @click="navigateTo(item.route)"
    >
      <span class="mobile-nav-icon-shell">
        <span v-if="item.svg" class="sb-btn-icon" v-html="item.svg" />
        <span v-else-if="item.icon" class="sb-btn-icon">
          <component :is="item.icon" :size="28" weight="regular" />
        </span>
      </span>
      <span class="mobile-nav-label">{{ item.label }}</span>
    </button>
    <button
      type="button"
      class="mobile-nav-item"
      :class="{ active: moreSheetOpen }"
      :aria-expanded="moreSheetOpen"
      @click="toggleMoreSheet"
    >
      <svg class="mobile-nav-icon" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M7 17.5L14 10.5L21 17.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      <span class="mobile-nav-label">More</span>
    </button>
  </nav>

  <!-- More Bottom Sheet -->
  <Transition name="more-sheet">
    <div v-if="moreSheetOpen" class="more-sheet-overlay" @click.self="moreSheetOpen = false">
      <div class="more-sheet" role="dialog" aria-modal="true" aria-labelledby="more-sheet-title">
        <div class="more-sheet__handle-row">
          <div class="more-sheet__handle" />
        </div>

        <h2 id="more-sheet-title" class="more-sheet__title">Nebeus products</h2>

        <div
          v-for="section in mobileProductSections"
          :key="section.label"
          class="more-sheet__section"
        >
          <p class="more-sheet__section-title">{{ section.label }}</p>
          <div
            class="more-sheet__product-grid"
            :class="{ 'more-sheet__product-grid--single': section.items.length === 1 }"
            :style="{ '--product-count': section.items.length }"
          >
            <button
              v-for="item in section.items"
              :key="item.name"
              class="more-sheet__product"
              type="button"
              @click="moreNavigate(item.route)"
            >
              <span class="more-sheet__product-icon">
                <span v-if="item.svg" class="sb-btn-icon" v-html="item.svg" />
                <span v-else-if="item.icon" class="sb-btn-icon">
                  <component :is="item.icon" :size="24" weight="regular" />
                </span>
                <span v-else class="sb-btn-icon sb-btn-icon--sub">
                  <span class="sb-sub-dot" />
                </span>
              </span>
              <span class="more-sheet__product-label">{{ item.label }}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </Transition>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import profileAvatarSvg from '@/assets/profile-avatar.svg';
import { CONFIG } from '@/const/config';
import {
  PhWallet,
  PhPaperPlaneTilt,
  PhCreditCard,
  PhArrowsDownUp,
  PhGear,
  PhCaretRight,
} from '@phosphor-icons/vue';

const sidebarRef = ref<HTMLElement | null>(null);
const isExpanded = ref(false);

const toggleSidebar = () => {
  isExpanded.value = !isExpanded.value;
};

const onClickOutside = (e: MouseEvent) => {
  if (isExpanded.value && sidebarRef.value && !sidebarRef.value.contains(e.target as Node)) {
    isExpanded.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', onClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', onClickOutside);
});

interface NavItem {
  name: string;
  label: string;
  route: string;
  icon?: any;
  svg?: string;
  children?: NavItem[];
}

interface MobileNavItem extends NavItem {
  activeKey: 'dashboard' | 'wallet' | 'trade';
}

interface Props {
  navItems?: NavItem[];
  footerItems?: NavItem[];
  userAvatar?: string;
  chatUnread?: number;
}

const props = withDefaults(defineProps<Props>(), {
  navItems: undefined,
  footerItems: undefined,
});

const { t } = useI18n();

const mobileDashboardSvg = '<svg viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M10.0625 5.25H17.9375" stroke="currentColor" stroke-width="1.5" stroke-linecap="square"/><path d="M7 9.625H21" stroke="currentColor" stroke-width="1.5" stroke-linecap="square"/><path d="M5.25 14C5.25 13.2751 5.83763 12.6875 6.5625 12.6875H21.4375C22.1624 12.6875 22.75 13.2751 22.75 14V23.1875C22.75 23.9124 22.1624 24.5 21.4375 24.5H6.5625C5.83763 24.5 5.25 23.9124 5.25 23.1875V14Z" stroke="currentColor" stroke-width="1.5"/></svg>';
const mobileWalletSvg = '<svg width="28" height="28" viewBox="0 0 28 28" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="11.375" cy="16.625" r="8" stroke="currentColor" stroke-width="1.5"/><mask id="path-2-inside-1_6351_51846" fill="white"><path fill-rule="evenodd" clip-rule="evenodd" d="M21.5721 14.1024C22.9675 13.1048 23.8771 11.4711 23.8771 9.625C23.8771 6.58743 21.4146 4.125 18.3771 4.125C16.5309 4.125 14.8973 5.03456 13.8997 6.43C13.3778 6.30128 12.8404 6.2116 12.291 6.16423C13.4957 4.05031 15.7699 2.625 18.3771 2.625C22.2431 2.625 25.3771 5.75901 25.3771 9.625C25.3771 12.2321 23.9518 14.5064 21.8378 15.711C21.7905 15.1616 21.7008 14.6243 21.5721 14.1024Z"/></mask><path d="M21.5721 14.1024L21.0632 13.3905L20.5804 13.7357L20.7225 14.3119L21.5721 14.1024ZM13.8997 6.43L13.6902 7.27955L14.2664 7.42165L14.6115 6.93886L13.8997 6.43ZM12.291 6.16423L11.5308 5.731L10.854 6.91857L12.2158 7.03599L12.291 6.16423ZM21.8378 15.711L20.9661 15.7862L21.0835 17.148L22.2711 16.4713L21.8378 15.711ZM22.0809 14.8142C23.6963 13.6594 24.7521 11.7655 24.7521 9.625H23.0021C23.0021 11.1767 22.2387 12.5502 21.0632 13.3905L22.0809 14.8142ZM24.7521 9.625C24.7521 6.10418 21.8979 3.25 18.3771 3.25V5C20.9314 5 23.0021 7.07068 23.0021 9.625H24.7521ZM18.3771 3.25C16.2366 3.25 14.3426 4.30579 13.1879 5.92115L14.6115 6.93886C15.4519 5.76333 16.8253 5 18.3771 5V3.25ZM12.2158 7.03599C12.7197 7.07943 13.2121 7.16165 13.6902 7.27955L14.1092 5.58046C13.5434 5.44092 12.9612 5.34376 12.3662 5.29246L12.2158 7.03599ZM18.3771 1.75C15.4429 1.75 12.8847 3.35512 11.5308 5.731L13.0512 6.59745C14.1066 4.7455 16.0969 3.5 18.3771 3.5V1.75ZM26.2521 9.625C26.2521 5.27576 22.7263 1.75 18.3771 1.75V3.5C21.7598 3.5 24.5021 6.24226 24.5021 9.625H26.2521ZM22.2711 16.4713C24.6469 15.1173 26.2521 12.5592 26.2521 9.625H24.5021C24.5021 11.9051 23.2566 13.8955 21.4046 14.9508L22.2711 16.4713ZM20.7225 14.3119C20.8404 14.79 20.9226 15.2824 20.9661 15.7862L22.7096 15.6359C22.6583 15.0409 22.5611 14.4587 22.4216 13.8928L20.7225 14.3119Z" fill="currentColor" mask="url(#path-2-inside-1_6351_51846)"/></svg>';

const computedNavItems = computed(() => props.navItems ?? [
  { name: 'dashboard', label: t('nav.dashboard'), route: '/app/dashboard', icon: PhWallet },
  { name: 'accounts', label: t('nav.accounts'), route: '/app/accounts', svg: '<svg width="24" height="24" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="13" cy="19" r="9" stroke="currentColor" stroke-width="2"/><mask id="path-2-inside-1_489_3849" fill="white"><path d="M21 3C25.4183 3 29 6.58172 29 11C29 13.9795 27.3698 16.5754 24.9541 17.9521C24.8908 17.2205 24.76 16.5085 24.5713 15.8203C26.0447 14.727 27 12.9756 27 11C27 7.68629 24.3137 5 21 5C19.0246 5.00012 17.2719 5.95459 16.1787 7.42773C15.4903 7.23912 14.7778 7.10807 14.0459 7.04492C15.4226 4.62932 18.0207 3.00014 21 3Z"/></mask><path d="M21 3V2H21L21 3ZM24.9541 17.9521L23.9578 18.0383L24.0924 19.5942L25.4493 18.821L24.9541 17.9521ZM24.5713 15.8203L23.9754 15.0172L23.426 15.4249L23.6069 16.0847L24.5713 15.8203ZM21 5V4H20.9999L21 5ZM16.1787 7.42773L15.9145 8.39219L16.5741 8.57293L16.9817 8.02366L16.1787 7.42773ZM14.0459 7.04492L13.1771 6.54976L12.4036 7.90694L13.9599 8.04122L14.0459 7.04492ZM21 3V4C24.866 4 28 7.13401 28 11H29H30C30 6.02944 25.9706 2 21 2V3ZM29 11H28C28 13.605 26.576 15.8768 24.4589 17.0833L24.9541 17.9521L25.4493 18.821C28.1637 17.2739 30 14.3539 30 11H29ZM24.9541 17.9521L25.9504 17.866C25.8817 17.0718 25.7397 16.3001 25.5357 15.5559L24.5713 15.8203L23.6069 16.0847C23.7802 16.7168 23.8999 17.3692 23.9578 18.0383L24.9541 17.9521ZM24.5713 15.8203L25.1672 16.6234C26.8835 15.3498 28 13.306 28 11H27H26C26 12.6453 25.2058 14.1042 23.9754 15.0172L24.5713 15.8203ZM27 11H28C28 7.13401 24.866 4 21 4V5V6C23.7614 6 26 8.23858 26 11H27ZM21 5L20.9999 4C18.6948 4.00014 16.6495 5.1153 15.3757 6.8318L16.1787 7.42773L16.9817 8.02366C17.8944 6.79388 19.3545 6.0001 21.0001 6L21 5ZM16.1787 7.42773L16.443 6.46328C15.6987 6.25937 14.9266 6.11719 14.1319 6.04862L14.0459 7.04492L13.9599 8.04122C14.629 8.09895 15.2819 8.21887 15.9145 8.39219L16.1787 7.42773ZM14.0459 7.04492L14.9147 7.54008C16.1209 5.42363 18.3947 4.00012 21 4L21 3L21 2C17.6468 2.00016 14.7243 3.835 13.1771 6.54976L14.0459 7.04492Z" fill="currentColor" mask="url(#path-2-inside-1_489_3849)"/></svg>' },
  { name: 'payments', label: t('nav.transactions'), route: '/app/payments', icon: PhPaperPlaneTilt, children: [
    { name: 'my-trades', label: t('trading.myTrades'), route: '/app/trades' },
  ] },
  // { name: 'cards', label: t('nav.cards'), route: '/app/cards', icon: PhCreditCard },
  // { name: 'crypto', label: t('nav.crypto'), route: '/app/crypto', svg: '...' },
  { name: 'trading', label: t('nav.trading'), route: '/app/crypto/trading', svg: '<svg width="29" height="28" viewBox="0 0 29 28" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 7C5.10457 7 6 7.89543 6 9V16C6 17.1046 5.10457 18 4 18H3C1.89543 18 1 17.1046 1 16V9C1 7.89543 1.89543 7 3 7H4Z" stroke="currentColor" stroke-width="2"/><path d="M15 10C16.1046 10 17 10.8954 17 12V22C17 23.1046 16.1046 24 15 24H14C12.8954 24 12 23.1046 12 22V12C12 10.8954 12.8954 10 14 10H15Z" stroke="currentColor" stroke-width="2"/><path d="M26 5C27.1046 5 28 5.89543 28 7V16C28 17.1046 27.1046 18 26 18H25C23.8954 18 23 17.1046 23 16V7C23 5.89543 23.8954 5 25 5H26Z" stroke="currentColor" stroke-width="2"/><line x1="14.5" y1="10" x2="14.5" y2="7" stroke="currentColor" stroke-width="2"/><line x1="3.5" y1="7" x2="3.5" y2="2" stroke="currentColor" stroke-width="2"/><line x1="3.5" y1="23" x2="3.5" y2="18" stroke="currentColor" stroke-width="2"/><line x1="25.5" y1="5" x2="25.5" stroke="currentColor" stroke-width="2"/><line x1="25.5" y1="23" x2="25.5" y2="18" stroke="currentColor" stroke-width="2"/><line x1="14.5" y1="28" x2="14.5" y2="24" stroke="currentColor" stroke-width="2"/></svg>' },
  { name: 'renting', label: t('nav.renting'), route: '/app/renting', svg: '<svg width="24" height="24" viewBox="0 0 256 256" fill="none" xmlns="http://www.w3.org/2000/svg"><polyline points="232 56 136 152 96 112 24 184" fill="none" stroke="currentColor" stroke-width="16" stroke-linecap="round" stroke-linejoin="round"></polyline><polyline points="232 120 232 56 168 56" fill="none" stroke="currentColor" stroke-width="16" stroke-linecap="round" stroke-linejoin="round"></polyline></svg>' },
  ...(CONFIG.LOAN_MODULE_ALLOWED ? [
    { name: 'loans', label: t('nav.loans'), route: '/app/loans', svg: '<svg width="24" height="24" viewBox="0 0 256 256" fill="none" xmlns="http://www.w3.org/2000/svg"><rect x="24" y="56" width="208" height="144" rx="16" fill="none" stroke="currentColor" stroke-width="16"/><line x1="24" y1="96" x2="232" y2="96" stroke="currentColor" stroke-width="16" stroke-linecap="round"/><line x1="56" y1="160" x2="112" y2="160" stroke="currentColor" stroke-width="16" stroke-linecap="round"/></svg>' },
  ] : []),
]);

const computedFooterItems = computed(() => props.footerItems ?? []);

const mobileBottomItems = computed<MobileNavItem[]>(() => {
  const navItems = computedNavItems.value;
  const byName = (name: string) => navItems.find(item => item.name === name);
  const wallet = byName('accounts') ?? { name: 'accounts', label: t('nav.accounts'), route: '/app/accounts', icon: PhWallet };
  const trade = byName('trading') ?? { name: 'trading', label: t('nav.trading'), route: '/app/crypto/trading', icon: PhWallet };

  return [
    {
      name: 'dashboard',
      label: 'Dashboard',
      route: '/app/dashboard',
      svg: mobileDashboardSvg,
      activeKey: 'dashboard',
    },
    {
      ...wallet,
      label: 'Wallet',
      svg: mobileWalletSvg,
      activeKey: 'wallet',
    },
    {
      ...trade,
      label: 'Trade',
      activeKey: 'trade',
    },
  ];
});

const mobileProductSections = computed(() => {
  const navItems = computedNavItems.value;
  const byName = (name: string) => navItems.find(item => item.name === name);
  const payments = byName('payments');
  const trades = payments?.children?.find(item => item.name === 'my-trades');
  const renting = byName('renting');
  const tradesItem: NavItem = {
    ...(trades ?? { name: 'my-trades', route: '/app/trades' }),
    label: 'Trades',
    icon: PhArrowsDownUp,
  };
  const settingsItem: NavItem = {
    name: 'settings',
    label: 'Settings',
    route: '/app/settings',
    icon: PhGear,
  };

  return [
    {
      label: 'Crypto',
      items: [renting].filter(Boolean) as NavItem[],
    },
    {
      label: 'Money',
      items: [payments, tradesItem, settingsItem].filter(Boolean) as NavItem[],
    },
  ];
});

defineEmits<{
  'help-clicked': [];
  'profile-clicked': [];
}>();

const router = useRouter();
const route = useRoute();

const moreSheetOpen = ref(false);

const isMobileNavActive = (section: 'dashboard' | 'wallet' | 'trade') => {
  if (section === 'dashboard') {
    return route.path === '/app/dashboard';
  }
  if (section === 'wallet') {
    return route.path === '/app/accounts' || route.path.startsWith('/app/account/');
  }
  return route.path === '/app/crypto/trading' || route.path.startsWith('/app/crypto/trading/');
};

const toggleMoreSheet = () => {
  moreSheetOpen.value = !moreSheetOpen.value;
};

const moreNavigate = (path: string) => {
  moreSheetOpen.value = false;
  router.push(path);
};

const navigateTo = (path: string) => {
  router.push(path);
};

const isActive = (path: string) => {
  if (route.path === path) return true;
  if (!route.path.startsWith(path + '/')) return false;
  // If a more specific nav route also matches, this parent should not be active
  const navPaths = [...computedNavItems.value, ...computedFooterItems.value].map(i => i.route);
  const hasMoreSpecificMatch = navPaths.some(p => p !== path && p.startsWith(path + '/') && route.path.startsWith(p));
  return !hasMoreSpecificMatch;
};

</script>

<style scoped lang="scss">
$sidebar-width: 64px;
$sidebar-expanded: 220px;
$icon-size: 48px;

.sidebar-wrapper {
  width: $sidebar-width;
  flex-shrink: 0;
  z-index: 100;
  align-self: stretch;
}

.floating-sidebar {
  position: fixed;
  top: 3rem;
  left: 3rem;
  height: calc(100vh - 6rem);
  width: $sidebar-width;
  background: var(--theme-sidebar-bg);
  border-radius: 16px;
  box-shadow: var(--theme-sidebar-shadow);
  transition: width 0.25s ease, box-shadow 0.25s ease;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  padding: 24px 0;

  &.expanded {
    width: $sidebar-expanded;
    box-shadow: var(--theme-sidebar-shadow-expanded);

    .sb-logo {
      padding-left: 20px;
      justify-content: flex-start;
    }

    .sb-btn {
      padding: 0 12px;
      justify-content: flex-start;
      margin: 0 8px;
      width: calc(100% - 16px);
      gap: 12px;
    }

    .sb-btn-label {
      opacity: 1;
      width: auto;
    }

    .sb-btn--sub {
      height: auto;
      min-height: 36px;
      opacity: 1;
      padding-left: 52px;
      font-size: 13px;
    }

    .sb-divider {
      margin-left: 8px;
      margin-right: 8px;
      width: calc(100% - 16px);
    }

    .sb-avatar {
      align-self: flex-start;
      margin-left: 16px;
    }
  }
}

.sb-logo {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 24px;
  padding: 0;
  flex-shrink: 0;
  transition: justify-content 0.25s ease, padding-left 0.25s ease;
  color: var(--theme-text-primary);

  svg {
    width: 32px;
    height: 32px;
    flex-shrink: 0;
  }
}

.sb-nav {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.sb-bottom {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: auto;
}

.sb-btn {
  height: $icon-size;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  background: transparent;
  border: none;
  outline: none;
  border-radius: 4px;
  color: var(--theme-text-primary);
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  white-space: nowrap;
  overflow: hidden;
  padding: 0;
  margin: 0 8px;
  width: calc(100% - 16px);

  &:not(.sb-toggle):hover {
    background: var(--theme-accent-bg);
  }

  &.active {
    background: var(--theme-accent-bg);

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 50%;
      transform: translateY(-50%);
      width: 3px;
      height: 24px;
      background: var(--theme-accent);
      border-radius: 0 3px 3px 0;
    }
  }

  &:not(.sb-toggle):active {
    background: var(--theme-accent-bg-active);
  }
}

.sb-btn-icon {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  :deep(svg) {
    width: 24px;
    height: 24px;
  }
}


.sb-btn-label {
  font-family: 'Manrope', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
  opacity: 0;
  width: 0;
  overflow: hidden;
  transition: opacity 0.2s ease, width 0.25s ease;
}

.sb-btn--sub {
  padding-left: 20px;
  height: 0;
  min-height: 0;
  opacity: 0;
  overflow: hidden;
  transition: height 0.2s ease, opacity 0.2s ease, min-height 0.2s ease;
}

.sb-btn-icon--sub {
  width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.sb-sub-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: var(--theme-text-secondary);
}

.sb-btn--sub.active .sb-sub-dot {
  background: var(--theme-green);
}

.sb-btn-icon--chat {
  position: relative;
}

.sb-chat-badge {
  position: absolute;
  top: -4px;
  right: -6px;
  min-width: 16px;
  height: 16px;
  border-radius: 8px;
  background: #FF4E59;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
  line-height: 1;
}

.sb-divider {
  height: 1px;
  background: var(--theme-sidebar-divider);
  margin: 8px auto;
  width: 32px;
  transition: all 0.25s ease;
}

.sb-avatar {
  cursor: pointer;
  transition: transform 0.2s ease;
  width: 45px;
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #bcff4eed;
  border-radius: 50%;
  border: 3px solid rgba(255, 255, 255, 0.5);
  flex-shrink: 0;
  box-sizing: content-box;
  margin: 0 auto;

  &:hover {
    transform: scale(1.05);
  }

  img {
    width: 22px;
    height: auto;
  }
}

.sb-toggle-rotated {
  transform: rotate(180deg);
}

.sb-toggle :deep(svg) {
  transition: transform 0.25s ease;
}

/* Mobile bottom nav - hidden on desktop */
.mobile-nav {
  display: none;
}

@media (max-width: 768px) {
  .floating-sidebar {
    display: none;
  }

  .mobile-nav {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 100;
    display: flex;
    align-items: center;
    gap: 8px;
    min-height: calc(100px + env(safe-area-inset-bottom));
    padding: 8px 16px calc(24px + env(safe-area-inset-bottom));
    box-sizing: border-box;
    background: var(--theme-bg-card);
    border-top: 1px solid var(--theme-border);
    box-shadow: none;
  }

  .mobile-nav-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    flex: 1 0 0;
    min-width: 0;
    min-height: 68px;
    background: none;
    border: none;
    outline: none;
    cursor: pointer;
    color: var(--theme-text-secondary);
    transition: color 0.2s ease;
    padding: 8px 0;

    .mobile-nav-icon {
      width: 28px;
      height: 28px;
      flex-shrink: 0;
    }

    .mobile-nav-icon-shell {
      width: 28px;
      height: 28px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;

      .sb-btn-icon {
        width: 24px;
        height: 24px;
      }

      .sb-btn-icon :deep(svg),
      .sb-btn-icon svg {
        width: 24px;
        height: 24px;
        flex-shrink: 0;
      }
    }

    .mobile-nav-label {
      font-family: 'Manrope', sans-serif;
      font-size: 12px;
      font-weight: 600;
      line-height: 16px;
      text-align: center;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &.active {
      color: var(--theme-text-primary);
    }
  }

  /* More bottom sheet */
  .more-sheet-overlay {
    position: fixed;
    inset: 0;
    z-index: 200;
    background: var(--theme-bg-overlay);
  }

  .more-sheet {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    display: flex;
    flex-direction: column;
    gap: 24px;
    min-height: 440px;
    max-height: calc(100svh - 24px);
    overflow-y: auto;
    background: var(--theme-bg-card);
    border-radius: 24px 24px 0 0;
    padding: 0 16px calc(56px + env(safe-area-inset-bottom));
    box-sizing: border-box;
  }

  .more-sheet__handle-row {
    position: relative;
    height: 16px;
    width: 100%;
    flex-shrink: 0;
  }

  .more-sheet__handle {
    width: 44px;
    height: 4px;
    background: var(--theme-border-strong);
    border-radius: 2px;
    margin: 14px auto 0;
  }

  .more-sheet__title {
    margin: 0;
    color: var(--theme-text-primary);
    font-family: 'Manrope', sans-serif;
    font-size: 24px;
    font-weight: 500;
    line-height: 32px;
  }

  .more-sheet__section {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .more-sheet__section-title {
    margin: 0;
    color: var(--theme-text-secondary);
    font-family: 'Manrope', sans-serif;
    font-size: 12px;
    font-weight: 500;
    line-height: 16px;
    text-transform: uppercase;
  }

  .more-sheet__product-grid {
    display: grid;
    grid-template-columns: repeat(var(--product-count), minmax(72px, 1fr));
    justify-content: stretch;
    column-gap: 8px;
    row-gap: 0;
    width: 100%;
  }

  .more-sheet__product-grid--single {
    grid-template-columns: 72px;
    justify-content: start;
  }

  .more-sheet__product {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 12px;
    width: 100%;
    padding: 0;
    background: transparent;
    border: none;
    outline: none;
    cursor: pointer;
    color: var(--theme-text-primary);
    text-align: center;
  }

  .more-sheet__product-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 72px;
    height: 72px;
    color: var(--theme-text-primary);
    background: var(--theme-bg-icon);
    border-radius: 999px;

    .sb-btn-icon {
      width: 24px;
      height: 24px;
    }

    .sb-btn-icon :deep(svg),
    .sb-btn-icon svg {
      width: 24px;
      height: 24px;
      flex-shrink: 0;
    }
  }

  .more-sheet__product-label {
    min-width: 100%;
    color: var(--theme-text-primary);
    font-family: 'Manrope', sans-serif;
    font-size: 14px;
    font-weight: 600;
    line-height: 16px;
  }

  /* Bottom sheet slide animation */
  .more-sheet-enter-active .more-sheet,
  .more-sheet-leave-active .more-sheet {
    transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  .more-sheet-enter-active,
  .more-sheet-leave-active {
    transition: background 0.3s ease;
  }

  .more-sheet-enter-from,
  .more-sheet-leave-to {
    background: rgba(0, 0, 0, 0);
  }

  .more-sheet-enter-from .more-sheet,
  .more-sheet-leave-to .more-sheet {
    transform: translateY(100%);
  }
}
</style>
